import { GetTopicResponse, LatLng, TopicCategory, TopicSearchResult } from '../types';
import { get, logWithAmplitude, throwHttpError } from './api/api';

import { Base64, SHA256 } from 'crypto-es';
import { DEFAULT_ICON_SIZE } from './constants';
import { getRandomColor } from './colors';
import organizationInfo from './state/organization-info';
import pointCache from './state/point-cache';

export async function isValidUrl(s: string) {

    // check if syntax is correct for URL
	s = s.trim();
    if(s.startsWith('#') || s.includes(' ')) {
        return false;
    }
    try {
		// eslint-disable-next-line no-new
		new URL(s);
	} catch {
		return false;
	}

    return await doesUrlExist(s);
}

const Timeout = (seconds: number) => {
	const controller = new AbortController();
	setTimeout(() => controller.abort(), seconds * 1000);
	return controller;
};

async function doesUrlExist(url: string) {
    const response = await fetch(url, {
        mode: 'no-cors',
        signal: Timeout(2).signal
    });

    return response !== undefined && (response.status < 400 || response.status >= 500);
}

export function getRedirectKeyFromUrl(url: string | undefined) {
    if (url === undefined) {
        return '';
    }

    const splits = url.split('/');

    return splits[splits.length - 1];
}

export function getQueryStringParameters(
    query: string = window.location.hash || window.location.search
) {
    const map = new Map<string, string>();

    const querySplits = query.startsWith('?') ? query.slice(1).split('?') : query.split('?');

    if (querySplits.length !== 2) {
        return map;
    }

    const kvps = querySplits[1].split('&');

    for (const kvp of kvps) {
        const [key, value] = kvp.split('=');
        map.set(decodeURIComponent(key), decodeURIComponent(value));
    }

    return map;
}

export function parseCategoryQueryString(categoryString: string): TopicCategory[] {
    const categoryJson = JSON.parse(categoryString) as any[];

    return categoryJson.map((cat: any) => {
        const icon = cat.icon !== undefined ? cat.icon : getRandomColor();
        const icon_size = cat.icon_size !== undefined ? cat.icon_size : DEFAULT_ICON_SIZE;

        return {
            _id: undefined,
            name: cat.name,
            description: cat.description,
            icon,
            icon_size,
            can_crowdsource: cat.can_crowdsource ?? true,
        };
    });
}

export async function getLocation(): Promise<LatLng> {
    return await new Promise((resolve, reject) => {
        if (navigator.geolocation !== undefined) {
            navigator.geolocation.getCurrentPosition((s) => {
                const l = {
                    lat: s.coords.latitude,
                    lng: s.coords.longitude,
                };
                pointCache.userLocation = l;
                resolve(l);
            }, reject);
        } else {
            reject(new Error('geolocation not available'));
        }
    });
}

export function calculateScreenshotHash(topic?: GetTopicResponse | TopicSearchResult): string {
    if (topic === undefined) {
        throw new Error('Topic is undefined');
    }
    const latStr = topic.default_map_center.latLng.lat.toString();
    const lastThreeDigits = latStr.slice(-3);
    const lat = parseFloat(lastThreeDigits);

    let pointCount = topic.statistics.approximate_point_count;
    // idont want to do the exact point count As the number gets bigger, I want to start rounding to the closest 10 then 100 then 1000
    // so that the hash is not too sensitive to small changes
    if (pointCount < 100) {
        pointCount = topic.statistics.approximate_point_count;
    } else if (pointCount < 1000) {
        pointCount = Math.round(pointCount / 10) * 10;
    } else if (pointCount < 10000) {
        pointCount = Math.round(pointCount / 100) * 100;
    } else {
        pointCount = Math.round(pointCount / 1000) * 1000;
    }

    const unhashedString = `${lat}&${topic.default_map_zoom}&${pointCount}`;

    // Run simple hash function
    const hash = SHA256(unhashedString);
    const hashedBytes = hash.toString(Base64);
    return hashedBytes.substring(0, 10);
}

export function redirectToLocation(location: string, delay: number) {
    setTimeout(() => {
        window.location.href = location;
    }, delay);
}

export async function getQRCode(amplitude_callsite: string, redirect_link: string) {
    const redirect_key = redirect_link.split('/').at(-1) ?? '';
    const response = await fetch(`/r/${redirect_key}/qr.png`, get());
    if (!response.ok) {
        await throwHttpError('An error occurred while retrieving the QR code.', response);
    }

    logWithAmplitude(amplitude_callsite, 'Get QR code', {
        redirect_key,
    });
    const qr_code = await response.blob();
    return window.URL.createObjectURL(qr_code);
}

export function ensureHTTPS(url?: string) {
    if (url === undefined) {
        return '';
    }

    const regex = /^(?!https:\/\/)(?!http:\/\/)[a-zA-Z0-9-.]+\.[a-zA-Z]{2,}(\/\S*)?$/;

    if (regex.test(url)) {
      return 'https://' + url;
    }

    return url;
  }

export function createWalletLink(wallet_card_id: string, email: string, first_name: string, image_url: string, points?: number) {

    const wallet_link = `https://wallet-pass.bambumeta.software/link-activation/${wallet_card_id}?email=${email}&firstName=${first_name}`;
    if (points !== undefined) {
        return `${wallet_link}&points=${points}&dynamicImageUrl=${image_url}`;
    }
    return wallet_link;
}

/**
 * Swaps a .proxi.co (or .azurewebsites.net) host with the org's verified custom domain.
 * Uses organizationInfo global state. Safe to call from any page — returns the original
 * URL unchanged if no custom domain is configured or verified.
 */
export function swapUrlForCustomDomain(url: string): string {
    const cname = organizationInfo.organization?.cname;
    if (cname?.validated !== true || !cname?.dns_verified || !cname?.custom_url) {
        return url;
    }
    try {
        const parsed = new URL(url);
        const isProxiHost = parsed.hostname.endsWith('.proxi.co')
            || parsed.hostname.endsWith('.azurewebsites.net')
            || parsed.hostname === 'localhost';
        if (isProxiHost) {
            const customHost = cname.custom_url.replace(/^https?:\/\//, '').split(/[/:?#]/)[0];
            parsed.hostname = customHost;
            parsed.protocol = 'https:';
            return parsed.toString();
        }
    } catch {
        // Not a valid URL — return as-is
    }
    return url;
}
